import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useApp } from '../../AppProvider';
import { effectiveRequiredFieldsForCountry, validateClientForContract } from '../../../lib/utils/validateClientForContract';
import { isRecognizedForeignCountry } from '../../../lib/utils/countries';
import { useAcceptanceDraft, type AcceptanceDraft } from '../../../hooks/deals/useAcceptanceDraft';
import { AcceptanceStepWhatsapp } from '../acceptance/AcceptanceStepWhatsapp';
import { AcceptanceStepContract } from '../acceptance/AcceptanceStepContract';
import { DrawerShell } from './DrawerShell';
import { AcceptanceChecklist, type ChecklistItem } from './AcceptanceChecklist';
import { ClientDataRows } from './ClientDataRows';
import type { Deal } from '../../../lib/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  deal: Deal;
  /** Dati attuali del cliente per il prefill (se non c'è bozza). */
  client: Record<string, any> | null;
  requiredFields: string[];
  clientName?: string;
  onCompleted: () => void;
}

type SectionKind = 'client' | 'whatsapp' | 'contract';

const CLIENT_FIELDS = [
  'business_name', 'vat_number', 'tax_code', 'sdi_code', 'pec', 'email',
  'email_amministrazione', 'street_address', 'street_number', 'postal_code', 'city', 'province',
];

/** Sezione a toggle controllato (per aprire/scrollare dalla checklist). */
function Section({ title, summary, open, onToggle, innerRef, children }: {
  title: string; summary?: string; open: boolean; onToggle: () => void;
  innerRef: React.RefObject<HTMLDivElement>; children: React.ReactNode;
}) {
  return (
    <div className="mt-1" ref={innerRef}>
      <button
        type="button" onClick={onToggle} aria-expanded={open}
        className="flex w-full items-center gap-1.5 rounded px-1.5 py-1.5 -mx-1.5 text-left hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] transition-colors"
      >
        <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 text-[#E91E8C] transition-transform ${open ? 'rotate-90' : ''}`} strokeWidth={2} />
        <span className="text-[11px] font-medium uppercase tracking-wide text-[rgba(55,53,47,0.5)] dark:text-gray-400">{title}</span>
        {!open && summary && <span className="ml-1.5 min-w-0 truncate text-[12.5px] text-[#787774] dark:text-gray-400">{summary}</span>}
      </button>
      {open && <div className="pt-1 pb-2">{children}</div>}
    </div>
  );
}

/**
 * Drawer "Accettazione contratto" (Parte C anagrafica internazionale):
 * sostituisce il wizard modale a step con checklist di stato in testa +
 * sezioni Dati cliente (a righe), Gruppo WhatsApp e Contratto. Stessa logica
 * (useAcceptanceDraft, nodo gruppo dai workflow, esoneri paese) del wizard.
 */
export function AcceptanceDrawer({ isOpen, onClose, deal, client, requiredFields, clientName, onCompleted }: Props) {
  const { toast } = useApp();
  const { busy, loadDraft, saveDraft, complete } = useAcceptanceDraft();

  // Lo step "Gruppo WhatsApp" ha senso solo se un workflow attivo crea davvero
  // il gruppo (nodo create_whatsapp_group). Stima iniziale dai campi richiesti,
  // poi corretta dalla query.
  const [hasGroupNode, setHasGroupNode] = useState<boolean>(requiredFields.includes('whatsapp_group'));
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: wfs } = await supabase.from('automation_workflows').select('id').eq('is_active', true);
      const ids = (wfs ?? []).map((w: { id: string }) => w.id);
      if (cancelled) return;
      if (ids.length === 0) { setHasGroupNode(false); return; }
      const { count } = await supabase
        .from('automation_workflow_steps')
        .select('id', { count: 'exact', head: true })
        .eq('step_type', 'create_whatsapp_group')
        .in('workflow_id', ids);
      if (!cancelled) setHasGroupNode((count ?? 0) > 0);
    })();
    return () => { cancelled = true; };
  }, []);

  const clientCountry = (client as any)?.country ?? null;
  const effectiveRequiredFields = useMemo(() => {
    const base = hasGroupNode ? requiredFields : requiredFields.filter((f) => f !== 'whatsapp_group');
    return effectiveRequiredFieldsForCountry(base, clientCountry);
  }, [hasGroupNode, requiredFields, clientCountry]);

  const hasClientSection = effectiveRequiredFields.some((f) => f !== 'whatsapp_group');

  const initial = useMemo<AcceptanceDraft>(() => {
    const draft = loadDraft(deal);
    if (draft) return draft;
    const c: Record<string, string> = {};
    for (const f of CLIENT_FIELDS) c[f] = (client?.[f] ?? '') as string;
    return {
      step: 0,
      client: c,
      whatsapp_group_numbers: Array.isArray((deal as any).whatsapp_group_numbers) ? (deal as any).whatsapp_group_numbers : [''],
      whatsapp_group_name: ((deal as any).whatsapp_group_name as string) || (clientName ? `${clientName} <> Obly` : ''),
      whatsapp_reuse_group_jid: ((deal as any).whatsapp_reuse_group_jid as string) || '',
      whatsapp_reuse_send_intro: (deal as any).whatsapp_reuse_send_intro !== false,
      pending_signed_contract_url: null,
      signing_date: null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id]);

  const [clientData, setClientData] = useState<Record<string, string>>(initial.client);
  const [numbers, setNumbers] = useState<string[]>(initial.whatsapp_group_numbers);
  const [groupName, setGroupName] = useState(initial.whatsapp_group_name);
  const [reuseJid, setReuseJid] = useState(initial.whatsapp_reuse_group_jid);
  const [sendIntro, setSendIntro] = useState<boolean>(initial.whatsapp_reuse_send_intro !== false);
  const [groupMode, setGroupModeState] = useState<'new' | 'existing'>(initial.whatsapp_reuse_group_jid ? 'existing' : 'new');
  const setGroupMode = (m: 'new' | 'existing') => {
    setGroupModeState(m);
    if (m === 'new') setReuseJid(''); // creando un nuovo gruppo non si riusa nulla
  };
  const [file, setFile] = useState<File | null>(null);
  const [signingDate, setSigningDate] = useState<Date>(initial.signing_date ? new Date(initial.signing_date) : new Date());
  const [showErrors, setShowErrors] = useState(false);
  const [open, setOpen] = useState<Record<SectionKind, boolean>>({ client: true, whatsapp: true, contract: true });
  const refs = {
    client: useRef<HTMLDivElement>(null),
    whatsapp: useRef<HTMLDivElement>(null),
    contract: useRef<HTMLDivElement>(null),
  };

  // Prefill dei dati cliente dal DB (currentDeal.client è parziale). Gira SEMPRE,
  // anche con bozza salvata: i campi vuoti nella bozza vengono ricompilati dal
  // record cliente (bug storico "alcuni dati non compilati").
  useEffect(() => {
    if (!deal.client_id) return;
    let cancelled = false;
    supabase
      .from('clients')
      .select('business_name, vat_number, tax_code, sdi_code, pec, email, email_amministrazione, street_address, street_number, postal_code, city, province, country')
      .eq('id', deal.client_id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data) return;
        setClientData((prev) => {
          const merged = { ...prev };
          for (const f of [...CLIENT_FIELDS, 'country']) {
            const dbVal = ((data as any)[f] ?? '') as string;
            // Il DB riempie solo i campi mancanti: non sovrascrive la bozza.
            if ((!merged[f] || merged[f].trim() === '') && dbVal) merged[f] = dbVal;
          }
          return merged;
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.client_id]);

  const waRequired = effectiveRequiredFields.includes('whatsapp_group');
  const waOk = !waRequired
    ? true
    : groupMode === 'existing'
      ? !!reuseJid
      : numbers.some((n) => n.trim() !== '') && !!groupName.trim();

  const validation = useMemo(() => {
    const record = {
      ...clientData,
      whatsapp_group_numbers: waOk ? ['ok'] : [],
      whatsapp_group_name: waOk ? 'ok' : '',
    };
    return validateClientForContract(record, effectiveRequiredFields);
  }, [clientData, waOk, effectiveRequiredFields]);

  const clientMissing = validation.missingFields.filter((f) => f !== 'whatsapp_group');
  const hasPdf = !!file || !!initial.pending_signed_contract_url;

  const goTo = (kind: SectionKind) => {
    setOpen((p) => ({ ...p, [kind]: true }));
    setTimeout(() => refs[kind].current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  const checklist: ChecklistItem[] = [
    ...(hasClientSection ? [{
      key: 'client',
      label: 'Dati cliente',
      ok: clientMissing.length === 0,
      summary: clientMissing.length === 0 ? 'completi' : `${clientMissing.length} ${clientMissing.length === 1 ? 'campo mancante' : 'campi mancanti'}`,
      error: showErrors && clientMissing.length > 0,
      onClick: () => goTo('client'),
    }] : []),
    ...(hasGroupNode ? [{
      key: 'whatsapp',
      label: 'Gruppo WhatsApp',
      ok: waOk,
      summary: !waRequired ? 'non richiesto' : reuseJid ? 'gruppo esistente' : groupName.trim() || 'da configurare',
      error: showErrors && !waOk,
      onClick: () => goTo('whatsapp'),
    }] : []),
    {
      key: 'contract',
      label: 'Contratto firmato',
      ok: hasPdf,
      summary: file
        ? file.name
        : initial.pending_signed_contract_url
          ? 'PDF in bozza'
          : 'PDF da caricare',
      error: showErrors && !hasPdf,
      onClick: () => goTo('contract'),
    },
  ];

  const buildDraft = (): AcceptanceDraft => ({
    step: 0,
    client: clientData,
    whatsapp_group_numbers: numbers.map((n) => n.trim()).filter((n) => n !== ''),
    whatsapp_group_name: groupName.trim(),
    whatsapp_reuse_group_jid: reuseJid,
    whatsapp_reuse_send_intro: sendIntro,
    pending_signed_contract_url: initial.pending_signed_contract_url,
    signing_date: `${signingDate.getFullYear()}-${String(signingDate.getMonth() + 1).padStart(2, '0')}-${String(signingDate.getDate()).padStart(2, '0')}`,
  });

  const onSaveDraft = async () => {
    const ok = await saveDraft(deal, buildDraft(), file);
    if (ok) { toast.success('Bozza salvata'); onCompleted(); onClose(); }
    else toast.error('Errore nel salvataggio della bozza');
  };

  const onComplete = async () => {
    // Validazione esplicita con feedback: mostra cosa manca e porta alla
    // sezione del primo requisito non soddisfatto.
    if (!validation.isValid) {
      setShowErrors(true);
      if (clientMissing.length > 0 && hasClientSection) goTo('client');
      else if (validation.missingFields.includes('whatsapp_group')) goTo('whatsapp');
      return;
    }
    if (!hasPdf) {
      setShowErrors(true);
      goTo('contract');
      return;
    }
    const ok = await complete(deal, buildDraft(), file);
    if (ok) { toast.success('Contratto accettato'); onCompleted(); onClose(); }
    else toast.error('Errore nel completamento della firma');
  };

  if (!isOpen) return null;

  return (
    <DrawerShell
      kicker="Accettazione contratto"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onSaveDraft} disabled={busy}
            className="rounded px-3 py-1.5 text-[12.5px] font-medium text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] disabled:opacity-50">
            Salva bozza
          </button>
          <span className="flex-1" />
          <button type="button" onClick={onComplete} disabled={busy}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded bg-[#1FA855] px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-[#178A45] disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Completa firma
          </button>
        </>
      }
    >
      {/* Titolo = cliente, per contesto */}
      <div className="mt-1 text-[17px] font-semibold text-[#37352F] dark:text-white truncate">
        {clientName || clientData.business_name || 'Cliente'}
      </div>

      <AcceptanceChecklist items={checklist} />

      {showErrors && !validation.isValid && (
        <p className="mt-2 text-[12px] text-[#EB5757]">Mancano: {validation.missingLabels.join(', ')}.</p>
      )}
      {showErrors && validation.isValid && !hasPdf && (
        <p className="mt-2 text-[12px] text-[#EB5757]">Carica il contratto firmato (PDF) per completare.</p>
      )}

      <div className="mt-3">
        {hasClientSection && (
          <Section title="Dati cliente" open={open.client} onToggle={() => setOpen((p) => ({ ...p, client: !p.client }))}
            summary={clientMissing.length === 0 ? 'completi' : `${clientMissing.length} da compilare`} innerRef={refs.client}>
            {isRecognizedForeignCountry(clientCountry) && (
              <p className="mb-2 rounded-md bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.08] px-3 py-2 text-[12px] text-[#6B6762] dark:text-gray-300">
                Cliente estero ({clientCountry}): Codice SDI, PEC e Codice Fiscale non sono richiesti.
              </p>
            )}
            <ClientDataRows
              values={clientData}
              onChange={(f, v) => setClientData((p) => ({ ...p, [f]: v }))}
              onChangeMany={(fields) => setClientData((p) => ({ ...p, ...fields }))}
              requiredFields={effectiveRequiredFields}
              showErrors={showErrors}
              missingFields={validation.missingFields}
              country={clientData.country || clientCountry}
            />
          </Section>
        )}

        {hasGroupNode && (
          <Section title="Gruppo WhatsApp" open={open.whatsapp} onToggle={() => setOpen((p) => ({ ...p, whatsapp: !p.whatsapp }))}
            summary={reuseJid ? 'gruppo esistente' : groupName.trim() || undefined} innerRef={refs.whatsapp}>
            <AcceptanceStepWhatsapp clientId={deal.client_id as string} mode={groupMode} onMode={setGroupMode}
              numbers={numbers} onNumbersChange={setNumbers} groupName={groupName} onGroupName={setGroupName}
              reuseJid={reuseJid} onReuseJid={setReuseJid} sendIntro={sendIntro} onSendIntro={setSendIntro}
              clientName={clientName} showErrors={showErrors} />
          </Section>
        )}

        <Section title="Contratto e firma" open={open.contract} onToggle={() => setOpen((p) => ({ ...p, contract: !p.contract }))}
          summary={hasPdf ? 'PDF pronto' : undefined} innerRef={refs.contract}>
          <AcceptanceStepContract file={file} onFile={setFile} pendingUrl={initial.pending_signed_contract_url}
            signingDate={signingDate} onSigningDate={setSigningDate} />
        </Section>
      </div>
    </DrawerShell>
  );
}
